import { QuestionnaireResponseItem } from '@medplum/fhirtypes'
import { BaseSubsectionAdapter } from 'components/Assessments/adapters/BaseSubsectionAdapter'
import { AdapterOptions, FlatAnswerMutation } from 'components/Assessments/adapters/types'
import { getAnswerValuesFromItems } from 'components/Assessments/helpers'
import { SocFormData } from 'components/Assessments/SocAssessment/types/SocFormData'
import { SocFormField } from 'components/Assessments/SocAssessment/types/SocFormField'
import { SocAssessmentQuestionnaireItem } from 'components/Assessments/SocAssessment/types/questionnaire'

/**
 * Gastrointestinal subsection adapter
 * Handles the most complex GI/GU assessment with feeding tubes, catheters, nutrition (20 fields)
 * This is the most complex subsection with many interdependent fields
 */
export class GastrointestinalAdapter extends BaseSubsectionAdapter<SocFormData> {
  extractFormData(
    flatAnswers: Record<string, QuestionnaireResponseItem>,
    _options?: AdapterOptions
  ): Partial<SocFormData> {
    const flatAnswersArray = Object.values(flatAnswers)

    // GI assessment
    const giAssessment = getAnswerValuesFromItems(
      flatAnswersArray,
      SocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_GASTROINTESTINAL_GI_ASSESSMENT
    )

    const bmDate = getAnswerValuesFromItems(
      flatAnswersArray,
      SocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_GASTROINTESTINAL_BM_DATE
    )?.at(0)

    // GU assessment
    const guAssessment = getAnswerValuesFromItems(
      flatAnswersArray,
      SocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_GASTROINTESTINAL_GU_ASSESSMENT
    )

    // Catheter assessment
    const catheter = getAnswerValuesFromItems(
      flatAnswersArray,
      SocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_GASTROINTESTINAL_CATHETER
    )?.at(0)

    const catheterType = getAnswerValuesFromItems(
      flatAnswersArray,
      SocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_GASTROINTESTINAL_CATHETER_TYPE
    )

    // Feeding tube assessment - complex with many related fields
    const feedingTube = getAnswerValuesFromItems(
      flatAnswersArray,
      SocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_GASTROINTESTINAL_FEEDING_TUBE
    )?.at(0)

    const feedingTubeAmount = getAnswerValuesFromItems(
      flatAnswersArray,
      SocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_GASTROINTESTINAL_FEEDING_TUBE_AMOUNT
    )?.at(0)

    const feedingTotalNumber = getAnswerValuesFromItems(
      flatAnswersArray,
      SocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_GASTROINTESTINAL_FEEDING_TOTAL_NUMBER
    )?.at(0)

    const feedingTubeDuration = getAnswerValuesFromItems(
      flatAnswersArray,
      SocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_GASTROINTESTINAL_FEEDING_TUBE_DURATION
    )?.at(0)

    const feedingTubeType = getAnswerValuesFromItems(
      flatAnswersArray,
      SocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_GASTROINTESTINAL_FEEDING_TUBE_TYPE
    )?.at(0)

    const feedingTubeSite = getAnswerValuesFromItems(
      flatAnswersArray,
      SocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_GASTROINTESTINAL_FEEDING_TUBE_SITE
    )

    const feedingTubeSize = getAnswerValuesFromItems(
      flatAnswersArray,
      SocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_GASTROINTESTINAL_FEEDING_TUBE_SIZE
    )?.at(0)

    const feedingTubeNotes = getAnswerValuesFromItems(
      flatAnswersArray,
      SocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_GASTROINTESTINAL_FEEDING_TUBE_NOTES
    )?.at(0)

    // Nutrition assessment
    const nutritionWnl = getAnswerValuesFromItems(
      flatAnswersArray,
      SocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_GASTROINTESTINAL_NUTRITION_WNL
    )?.at(0)

    const nutritionalFactors = getAnswerValuesFromItems(
      flatAnswersArray,
      SocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_GASTROINTESTINAL_NUTRITIONAL_FACTORS
    )

    const dietRequirements = getAnswerValuesFromItems(
      flatAnswersArray,
      SocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_GASTROINTESTINAL_DIET_REQUIREMENTS
    )?.at(0)

    const nutritionNotes = getAnswerValuesFromItems(
      flatAnswersArray,
      SocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_GASTROINTESTINAL_NUTRITION_NOTES
    )?.at(0)

    const nutritionalScreening = getAnswerValuesFromItems(
      flatAnswersArray,
      SocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_GASTROINTESTINAL_NUTRITIONAL_SCREENING
    )

    const historyNotes = getAnswerValuesFromItems(
      flatAnswersArray,
      SocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_GASTROINTESTINAL_GASTROINTESTINAL_HISTORY_NOTES
    )?.at(0)

    const systemSummary = getAnswerValuesFromItems(
      flatAnswersArray,
      SocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_GASTROINTESTINAL_SYSTEM_SUMMARY
    )?.at(0)

    return {
      [SocFormField.GASTROINTESTINAL_GI_ASSESSMENT]: giAssessment,
      [SocFormField.GASTROINTESTINAL_BM_DATE]: bmDate,
      [SocFormField.GASTROINTESTINAL_GU_ASSESSMENT]: guAssessment,
      [SocFormField.GASTROINTESTINAL_CATHETER]: catheter,
      [SocFormField.GASTROINTESTINAL_CATHETER_TYPE]: catheterType,
      [SocFormField.GASTROINTESTINAL_FEEDING_TUBE]: feedingTube,
      [SocFormField.GASTROINTESTINAL_FEEDING_TUBE_AMOUNT]: feedingTubeAmount as number,
      [SocFormField.GASTROINTESTINAL_FEEDING_TOTAL_NUMBER]: feedingTotalNumber as number,
      [SocFormField.GASTROINTESTINAL_FEEDING_TUBE_DURATION]: feedingTubeDuration,
      [SocFormField.GASTROINTESTINAL_FEEDING_TUBE_TYPE]: feedingTubeType,
      [SocFormField.GASTROINTESTINAL_FEEDING_TUBE_SITE]: feedingTubeSite,
      [SocFormField.GASTROINTESTINAL_FEEDING_TUBE_SIZE]: feedingTubeSize,
      [SocFormField.GASTROINTESTINAL_FEEDING_TUBE_NOTES]: feedingTubeNotes,
      [SocFormField.GASTROINTESTINAL_NUTRITION_WNL]: nutritionWnl,
      [SocFormField.GASTROINTESTINAL_NUTRITIONAL_FACTORS]: nutritionalFactors,
      [SocFormField.GASTROINTESTINAL_DIET_REQUIREMENTS]: dietRequirements,
      [SocFormField.GASTROINTESTINAL_NUTRITION_NOTES]: nutritionNotes,
      [SocFormField.GASTROINTESTINAL_NUTRITIONAL_SCREENING]: nutritionalScreening,
      [SocFormField.GASTROINTESTINAL_HISTORY_NOTES]: historyNotes,
      [SocFormField.GASTROINTESTINAL_SYSTEM_SUMMARY]: systemSummary
    }
  }

  buildFlatAnswers(): FlatAnswerMutation[] {
    const mutations: FlatAnswerMutation[] = [
      {
        linkId: SocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_GASTROINTESTINAL_GI_ASSESSMENT,
        value: this.getFormDataField(SocFormField.GASTROINTESTINAL_GI_ASSESSMENT)
      },
      {
        linkId: SocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_GASTROINTESTINAL_BM_DATE,
        value: this.getFormDataField(SocFormField.GASTROINTESTINAL_BM_DATE)
      },
      {
        linkId: SocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_GASTROINTESTINAL_GU_ASSESSMENT,
        value: this.getFormDataField(SocFormField.GASTROINTESTINAL_GU_ASSESSMENT)
      },
      {
        linkId: SocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_GASTROINTESTINAL_CATHETER,
        value: this.getFormDataField(SocFormField.GASTROINTESTINAL_CATHETER)
      },
      {
        linkId: SocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_GASTROINTESTINAL_FEEDING_TUBE,
        value: this.getFormDataField(SocFormField.GASTROINTESTINAL_FEEDING_TUBE)
      },
      {
        linkId: SocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_GASTROINTESTINAL_NUTRITION_WNL,
        value: this.getFormDataField(SocFormField.GASTROINTESTINAL_NUTRITION_WNL)
      },
      {
        linkId:
          SocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_GASTROINTESTINAL_NUTRITIONAL_SCREENING,
        value: this.getFormDataField(SocFormField.GASTROINTESTINAL_NUTRITIONAL_SCREENING)
      },
      {
        linkId: SocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_GASTROINTESTINAL_SYSTEM_SUMMARY,
        value: this.getFormDataField(SocFormField.GASTROINTESTINAL_SYSTEM_SUMMARY)
      }
    ]

    // Conditionally add catheter type if it exists (conditionally rendered when catheter === 'Yes')
    if (this.formDataFieldExists(SocFormField.GASTROINTESTINAL_CATHETER_TYPE)) {
      mutations.push({
        linkId: SocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_GASTROINTESTINAL_CATHETER_TYPE,
        value: this.getFormDataField(SocFormField.GASTROINTESTINAL_CATHETER_TYPE)
      })
    }

    // Conditionally add feeding tube related fields if they exist (conditionally rendered when feeding tube === 'Yes')
    if (this.formDataFieldExists(SocFormField.GASTROINTESTINAL_FEEDING_TUBE_AMOUNT)) {
      mutations.push({
        linkId:
          SocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_GASTROINTESTINAL_FEEDING_TUBE_AMOUNT,
        value: this.getFormDataField(SocFormField.GASTROINTESTINAL_FEEDING_TUBE_AMOUNT)
      })
    }

    if (this.formDataFieldExists(SocFormField.GASTROINTESTINAL_FEEDING_TOTAL_NUMBER)) {
      mutations.push({
        linkId:
          SocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_GASTROINTESTINAL_FEEDING_TOTAL_NUMBER,
        value: this.getFormDataField(SocFormField.GASTROINTESTINAL_FEEDING_TOTAL_NUMBER)
      })
    }

    if (this.formDataFieldExists(SocFormField.GASTROINTESTINAL_FEEDING_TUBE_DURATION)) {
      mutations.push({
        linkId:
          SocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_GASTROINTESTINAL_FEEDING_TUBE_DURATION,
        value: this.getFormDataField(SocFormField.GASTROINTESTINAL_FEEDING_TUBE_DURATION)
      })
    }

    if (this.formDataFieldExists(SocFormField.GASTROINTESTINAL_FEEDING_TUBE_TYPE)) {
      mutations.push({
        linkId:
          SocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_GASTROINTESTINAL_FEEDING_TUBE_TYPE,
        value: this.getFormDataField(SocFormField.GASTROINTESTINAL_FEEDING_TUBE_TYPE)
      })
    }

    if (this.formDataFieldExists(SocFormField.GASTROINTESTINAL_FEEDING_TUBE_SITE)) {
      mutations.push({
        linkId:
          SocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_GASTROINTESTINAL_FEEDING_TUBE_SITE,
        value: this.getFormDataField(SocFormField.GASTROINTESTINAL_FEEDING_TUBE_SITE)
      })
    }

    if (this.formDataFieldExists(SocFormField.GASTROINTESTINAL_FEEDING_TUBE_SIZE)) {
      mutations.push({
        linkId:
          SocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_GASTROINTESTINAL_FEEDING_TUBE_SIZE,
        value: this.getFormDataField(SocFormField.GASTROINTESTINAL_FEEDING_TUBE_SIZE)
      })
    }

    if (this.formDataFieldExists(SocFormField.GASTROINTESTINAL_FEEDING_TUBE_NOTES)) {
      mutations.push({
        linkId:
          SocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_GASTROINTESTINAL_FEEDING_TUBE_NOTES,
        value: this.getFormDataField(SocFormField.GASTROINTESTINAL_FEEDING_TUBE_NOTES)
      })
    }

    // Conditionally add nutritional factors if they exist (conditionally rendered when nutrition WNL !== 'Yes')
    if (this.formDataFieldExists(SocFormField.GASTROINTESTINAL_NUTRITIONAL_FACTORS)) {
      mutations.push({
        linkId:
          SocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_GASTROINTESTINAL_NUTRITIONAL_FACTORS,
        value: this.getFormDataField(SocFormField.GASTROINTESTINAL_NUTRITIONAL_FACTORS)
      })
    }

    if (this.formDataFieldExists(SocFormField.GASTROINTESTINAL_DIET_REQUIREMENTS)) {
      mutations.push({
        linkId:
          SocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_GASTROINTESTINAL_DIET_REQUIREMENTS,
        value: this.getFormDataField(SocFormField.GASTROINTESTINAL_DIET_REQUIREMENTS)
      })
    }

    if (this.formDataFieldExists(SocFormField.GASTROINTESTINAL_NUTRITION_NOTES)) {
      mutations.push({
        linkId: SocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_GASTROINTESTINAL_NUTRITION_NOTES,
        value: this.getFormDataField(SocFormField.GASTROINTESTINAL_NUTRITION_NOTES)
      })
    }

    if (this.formDataFieldExists(SocFormField.GASTROINTESTINAL_HISTORY_NOTES)) {
      mutations.push({
        linkId:
          SocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_GASTROINTESTINAL_GASTROINTESTINAL_HISTORY_NOTES,
        value: this.getFormDataField(SocFormField.GASTROINTESTINAL_HISTORY_NOTES)
      })
    }

    return mutations
  }
}
